import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type KvkkStatus =
  | { type: 'unknown'; loading: boolean }
  | { type: 'no_record'; loading: boolean }
  | {
      type: 'granted' | 'declined' | 'denied' | 'withdrawn';
      loading: boolean;
      policyVersionId: string | null;
      grantedAt?: string | null;
    };

type ConsentStatusValue = 'granted' | 'declined' | 'denied' | 'withdrawn';

const normalizeConsentStatus = (rawStatus: string | null | undefined): ConsentStatusValue | null => {
  if (!rawStatus) return null;
  if (rawStatus === 'granted') return 'granted';
  if (rawStatus === 'withdrawn') return 'withdrawn';
  if (rawStatus === 'declined') return 'declined';
  if (rawStatus === 'denied') return 'denied';
  return null;
};

export function useKvkkStatus(phone: string | null, refreshToken?: number): KvkkStatus {
  const [status, setStatus] = useState<KvkkStatus>({ type: 'unknown', loading: false });

  useEffect(() => {
    // If phone is null or empty, return unknown status
    if (!phone || phone.trim() === '') {
      setStatus({ type: 'unknown', loading: false });
      return;
    }

    const fetchStatus = async () => {
      console.log('📊 [KVKK Status Hook] Fetching status for phone:', phone);
      setStatus(prev => ({ ...prev, loading: true }));

      try {
        // Primary source: append-only ledger ordered by latest event.
        // This keeps banner/blocking logic aligned with KVKK detail section.
        console.log('📊 [KVKK Status Hook] Calling RPC get_kvkk_consent_history');
        const { data: ledgerData, error: ledgerError } = await supabase.rpc('get_kvkk_consent_history', {
          p_phone: phone,
        });

        if (!ledgerError && ledgerData && ledgerData.length > 0) {
          const latest = ledgerData[0] as {
            consent_status: string;
            policy_version_id: string | null;
            created_at: string;
          };

          const normalizedStatus = normalizeConsentStatus(latest.consent_status);
          if (normalizedStatus) {
            console.log('📊 [KVKK Status Hook] Ledger status found:', normalizedStatus, latest);
            setStatus({
              type: normalizedStatus,
              loading: false,
              policyVersionId: latest.policy_version_id || null,
              grantedAt: normalizedStatus === 'granted' ? latest.created_at : null,
            });
            return;
          }
        }

        if (ledgerError) {
          console.warn('⚠️ [KVKK Status Hook] Ledger RPC failed, falling back to kvkk_get_status_for_phone:', ledgerError);
        }

        // Fallback for legacy deployments that may not have ledger rows yet.
        console.log('📊 [KVKK Status Hook] Calling fallback RPC kvkk_get_status_for_phone');
        const { data, error } = await supabase.rpc('kvkk_get_status_for_phone', {
          p_phone: phone,
        });

        if (error) {
          console.error('❌ [KVKK Status Hook] Error fetching KVKK status:', error);
          console.error('❌ [KVKK Status Hook] Error details:', JSON.stringify(error, null, 2));
          setStatus({ type: 'unknown', loading: false });
          return;
        }

        // If no rows returned, there's no consent record
        if (!data || data.length === 0) {
          console.log('📊 [KVKK Status Hook] No consent record found');
          setStatus({ type: 'no_record', loading: false });
          return;
        }

        // Map consent_status to type (backward compatible with legacy "denied")
        const row = data[0];
        const consentStatus = normalizeConsentStatus(row.consent_status);
        if (!consentStatus) {
          setStatus({ type: 'unknown', loading: false });
          return;
        }
        console.log('📊 [KVKK Status Hook] Consent status found:', consentStatus, row);

        setStatus({
          type: consentStatus,
          loading: false,
          policyVersionId: row.policy_version_id || null,
          grantedAt: row.granted_at || null,
        });
      } catch (err) {
        console.error('❌ [KVKK Status Hook] Exception in useKvkkStatus:', err);
        console.error('❌ [KVKK Status Hook] Exception details:', err instanceof Error ? {
          name: err.name,
          message: err.message,
          stack: err.stack,
        } : err);
        setStatus({ type: 'unknown', loading: false });
      }
    };

    void fetchStatus();
  }, [phone, refreshToken]);

  return status;
}

